import { useList, useCustomMutation, useDelete, useInvalidate } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import { Plus, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PricingTiersList } from '@/components/shared/PricingTiersList';
import { notify } from '@/lib/notify';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DataTable,
  TableToolbar,
  FilterSelect,
  type Column,
  type SortState,
  type BulkAction,
} from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { resolveApiUrl } from '@/providers/api-url';
import {
  type SubscriptionPlan,
  getSubscriptionPlanName,
  formatDuration,
} from './types';

const API_URL = resolveApiUrl();

function formatDate(dateString: string | null): string {
  if (!dateString) {return '\u2014';}
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}


export function SubscriptionPlansList() {
  useScrollRestoration();
  const navigate = useNavigate();
  const [page, setPage] = usePersistedState('subscription-plans:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('subscription-plans:sort', { field: 'order', order: 'asc' });
  const [statusFilter, setStatusFilter] = usePersistedState('subscription-plans:statusFilter', 'all');
  const [search, setSearch] = usePersistedState('subscription-plans:search', '');
  const pageSize = 20;

  const queryFilters = [
    ...(search ? [{ field: 'search', operator: 'contains' as const, value: search }] : []),
    ...(statusFilter !== 'all' ? [{ field: 'isActive', operator: 'eq' as const, value: statusFilter === 'true' }] : []),
  ];

  const { result, query } = useList<SubscriptionPlan>({
    resource: 'subscription-plans',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: queryFilters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const { mutate: bulkAction } = useCustomMutation();
  const { mutate: deletePlan } = useDelete();
  const invalidate = useInvalidate();
  const [deleteItems, setDeleteItems] = useState<SubscriptionPlan[]>([]);

  const plans = result?.data ?? [];
  const total = result?.total ?? 0;
  const isLoading = query.isLoading;

  const handleBulkActivate = (items: SubscriptionPlan[]) => {
    const ids = items.map((item) => item.id);
    bulkAction(
      {
        url: `${API_URL}/subscription-plans/admin/bulk/activate`,
        method: 'post',
        values: { ids },
      },
      {
        onSuccess: () => {
          notify.success(`Активировано планов: ${ids.length}`);
          query.refetch();
        },
        onError: (error) => {
          notify.error(`Ошибка активации: ${error.message}`);
        },
      },
    );
  };

  const handleBulkDeactivate = (items: SubscriptionPlan[]) => {
    const ids = items.map((item) => item.id);
    bulkAction(
      {
        url: `${API_URL}/subscription-plans/admin/bulk/deactivate`,
        method: 'post',
        values: { ids },
      },
      {
        onSuccess: () => {
          notify.success(`Деактивировано планов: ${ids.length}`);
          query.refetch();
        },
        onError: (error) => {
          notify.error(`Ошибка деактивации: ${error.message}`);
        },
      },
    );
  };

  const handleBulkDelete = () => {
    if (deleteItems.length === 0) {return;}

    for (const item of deleteItems) {
      deletePlan(
        { resource: 'subscription-plans', id: item.id },
        {
          onSuccess: () => {
            invalidate({ resource: 'subscription-plans', invalidates: ['list'] });
          },
        },
      );
    }
    setDeleteItems([]);
  };

  const columns: Column<SubscriptionPlan>[] = [
    {
      key: 'code',
      header: 'Код',
      sortable: true,
      render: (_, plan) => (
        <span className="font-mono text-sm">{plan.code}</span>
      ),
    },
    {
      key: 'name',
      header: 'Название',
      render: (_, plan) => (
        <span className="font-medium">{getSubscriptionPlanName(plan)}</span>
      ),
    },
    {
      key: 'pricingTiers',
      header: 'Тарифы',
      render: (_, plan) => (
        <PricingTiersList
          tiers={plan.pricingTiers}
          onTierClick={(tier) => navigate(`/subscription-plans/show/${plan.id}?tab=pricing&tier=${tier.id}`)}
        />
      ),
    },
    {
      key: 'durationDays',
      header: 'Срок',
      sortable: true,
      render: (_, plan) => (
        <span className="text-sm">{formatDuration(plan.durationDays)}</span>
      ),
    },
    {
      key: 'isActive',
      header: 'Статус',
      sortable: true,
      render: (_, plan) =>
        plan.isActive ? (
          <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
            Активен
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-gray-500/10 text-gray-500 border-gray-500/20">
            Неактивен
          </Badge>
        ),
    },
    {
      key: 'createdAt',
      header: 'Создан',
      sortable: true,
      render: (_, plan) => (
        <span className="text-sm text-muted-foreground">{formatDate(plan.createdAt)}</span>
      ),
    },
  ];

  const bulkActions: BulkAction<SubscriptionPlan>[] = [
    {
      label: 'Активировать',
      icon: <CheckCircle className="h-4 w-4" />,
      onClick: handleBulkActivate,
      showOnlyWhenSelected: true,
    },
    {
      label: 'Деактивировать',
      icon: <XCircle className="h-4 w-4" />,
      onClick: handleBulkDeactivate,
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
  ];

  const filtersCount = statusFilter !== 'all' ? 1 : 0;

  return (
    <div className="space-y-6">
      <FormHeader
        title="Планы Masters Club"
        actions={
          <Button onClick={() => navigate('/subscription-plans/create')}>
            <Plus className="h-4 w-4 mr-2" />
            Создать план
          </Button>
        }
        sticky
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder="Поиск по коду или названию..."
        filters={
          <div className="grid gap-4 sm:grid-cols-2">
            <FilterSelect
              label="Статус"
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
              options={[
                { value: 'true', label: 'Активные' },
                { value: 'false', label: 'Неактивные' },
              ]}
              allLabel="Все статусы"
            />
          </div>
        }
        filtersCount={filtersCount}
        onResetFilters={() => {
          setStatusFilter('all');
          setPage(1);
        }}
      />

      <DataTable
        columns={columns}
        data={plans}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(plan) => navigate(`/subscription-plans/show/${plan.id}`)}
        emptyMessage="Планы не найдены"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1
                ? 'Удалить план подписки?'
                : `Удалить ${deleteItems.length} планов подписки?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1 ? (
                <>
                  Вы уверены, что хотите удалить план «{getSubscriptionPlanName(deleteItems[0])}»?
                  Это действие нельзя отменить.
                </>
              ) : (
                <>
                  Вы уверены, что хотите удалить {deleteItems.length} планов?
                  Это действие нельзя отменить.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
